<template>
  <div>
    <div
      v-if="description"
      class="block aisea-text-14-400-dark-gray text-sm text-left font-bold mb-2"
    >
      {{ description }}
    </div>
    <div v-if="!selectedFiles" class="m-1 pt-2">
      <span class="aisea-text-14-400-gray">{{ acceptFileType }}</span>
    </div>
    <div
      v-if="files.length === 0"
      class="file-upload-container"
      :class="{ 'drag-over': isDragOver }"
      @dragover.prevent="handleDragOver"
      @dragleave.prevent="handleDragLeave"
      @drop.prevent="handleDrop"
      @click="files.length === 0 && triggerFileInput"
    >
      <div>
        <p>
          <span class="block mb-2">ファイルをここにドラッグ&ドロップ</span>
          <span class="block mb-2">または</span>
        </p>
        <button class="custom-button" @click="triggerFileInput">
          <div class="flex justify-center items-center gap-2">
            <feather-icon icon="FolderIcon" svgClasses="h-4 w-4" />
            <span class="">ファイルを選択</span>
          </div>
        </button>
        <input
          :multiple="isMultiple"
          :accept="accept"
          type="file"
          ref="fileInput"
          @change="handleFileSelect"
          hidden
        />
      </div>
    </div>

    <!-- 選択されたファイルリスト -->
    <div v-if="selectedFiles">
      <div class="msg-container">
        <div class="file-info aisea-text-14-700-dark-gray">
          {{ files.length }}つのファイルが選択されました
        </div>
        <div>
          合計サイズ: <strong>{{ totalSize }}</strong>
          <span v-if="limitFileSize">
            (制限: {{ convertBytes(limitFileSize) }})</span
          >
        </div>
      </div>
      <TransitionGroup
        name="list"
        tag="ul"
        class="file-list-container flex flex-col gap-5"
      >
        <li v-for="(file, index) in files" :key="file.name">
          <div
            class="flex flex-row gap-5 p-5"
            :class="{
              'transition-opacity duration-500': selectedFiles,
            }"
          >
            <feather-icon
              icon="CheckCircleIcon"
              svgClasses="h-6 w-6"
              class="aisea-text-16-400-blue"
            />
            <span>{{ file.name }}</span>
            <span class="ml-auto">{{ convertBytes(file.size) }}</span>
            <feather-icon
              icon="XIcon"
              svgClasses="h-4 w-4"
              class="cursor-pointer"
              @click="removeFile(index)"
            />
          </div>
        </li>
      </TransitionGroup>
    </div>
    <!-- エラー表示 -->
    <div v-if="errorMessage" class="error-message-container p-4">
      <div class="flex justify-start items-center gap-2 aisea-text-14-700-red">
        <feather-icon icon="AlertTriangleIcon" svgClasses="h-6 w-6" />
        <span>このファイルはアップロードできません。</span>
      </div>
      <div class="aisea-text-14-400-red">{{ errorMessage }}</div>
    </div>
  </div>
</template>
<script>
export default {
  name: "DropZone",
  props: {
    id: { type: String, required: true },
    description: { type: String, default: "" },
    isMultiple: { type: Boolean, default: true },
    limitFileSize: { type: Number, default: null },
    accept: { type: Array, default: () => [] },
    acceptMimeType: { type: Array, default: () => [] },
  },
  data() {
    return {
      files: [],
      errorMessage: "",
      isDragOver: false,
    };
  },
  computed: {
    selectedFiles() {
      return this.files.length > 0;
    },
    acceptFileType() {
      return `ファイル形式は${this.accept.join(
        " "
      )}のみです。(${this.convertBytes(this.limitFileSize)}以内)`;
    },
    totalSize() {
      const total = this.files.reduce((sum, file) => sum + file.size, 0);
      return this.convertBytes(total);
    },
  },
  methods: {
    triggerFileInput() {
      this.$refs.fileInput.click();
    },
    handleFileSelect(event) {
      this.addFiles(Array.from(event.target.files));
    },
    handleDrop(event) {
      this.isDragOver = false;
      this.addFiles(Array.from(event.dataTransfer.files));
    },
    handleDragOver() {
      this.isDragOver = true;
    },
    handleDragLeave() {
      this.isDragOver = false;
    },
    addFiles(files) {
      this.errorMessage = "";

      const validFiles = files.filter(file => {
        if (!this.acceptMimeType.includes(file.type)) {
          this.errorMessage = `${this.accept.join(
            " "
          )}ファイルのみがサポートされています。`;
          return false;
        }
        return true;
      });

      if (validFiles.length > 0) {
        this.files = this.files.concat(validFiles);
        this.emitFormData();
      }
    },
    removeFile(index) {
      this.files.splice(index, 1);
      this.emitFormData();
    },
    convertBytes(valueInBytes) {
      const units = ["B", "KB", "MB", "GB", "TB"];
      let size = valueInBytes;
      let unitIndex = 0;

      while (size >= 1024 && unitIndex < units.length - 1) {
        size /= 1024;
        unitIndex++;
      }

      return `${Math.round(size)} ${units[unitIndex]}`;
    },
    emitFormData() {
      // 親に通知
      this.$emit("input", { id: this.id, value: this.files });
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/aisea/_variables.scss";

.file-upload-container {
  height: 200px;
  border: 2px dashed #ccc;
  border-radius: 8px;
  background: #f5f5f5;
  padding: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  color: #706d65;
}

.file-upload-container.drag-over {
  border-color: #3498db;
}

.file-list-container {
  height: 100%;
  padding-top: 5px;
  padding-bottom: 5px;
  overflow: scroll;
}

.msg-container {
  margin: 10px;
  display: flex;
  justify-content: space-between;
}

.file-list-container > li {
  background: #f5f5f5;
}

.error-message-container {
  margin-top: 20px;
  border: 2px solid $aisea-red;
  border-radius: 8px;
  background: #fff0f2;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.custom-button {
  height: 2.5rem;
  padding: var(--Margin-m-15, 0.375rem) 1.125rem;
  border: 1px solid #3498db;
  border-radius: 8px;
  color: #039be5;
  cursor: pointer;
  transition: background-color 0.3s ease, color 0.3s ease;
}

.custom-button:hover {
  background-color: #039be5;
  color: #fff;
}

.list-move,
.list-enter-active,
.list-leave-active {
  transition: all 0.5s ease;
}

.list-enter-from,
.list-leave-to {
  opacity: 0;
  transform: translateX(-30px);
}

.list-leave-active {
  position: relative;
}
</style>